import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DepartureTimeFilter from "./FilterTypes/DepartureTimeFilter";
import DurationFilter from "./FilterTypes/DurationFilter";
import BaggageAllowance from "./FilterTypes/BaggageAllowanceFilter";
import Stops from "./FilterTypes/StopsFilter";
import PreferredAirlinesFilter from "./FilterTypes/PreferredAirlinesFilter";
import TravellerTypesFilter from "./FilterTypes/TravellerTypesFilter";
import ClearFiltersButton from "./FilterOptions/ClearFiltersButton";
import { filterSelector } from "redux/filters/filters.selector";
import { useCountUp } from "use-count-up";
import { useTranslations } from "hooks/useTranslations";
import { useSelector } from "react-redux";
import { sortSelector } from "redux/sort";

export default function DesktopFilters() {
  const { translations } = useTranslations();

  const [currentCount, setCurrentCount] = useState(0);
  const { allAirlines } = useSelector(filterSelector);
  const { sortType, filteredIndexes } = useSelector(sortSelector);

  useEffect(() => {
    reset();
  }, [filteredIndexes]);

  const { value, reset } = useCountUp({
    isCounting: true,
    start: currentCount,
    end: filteredIndexes[sortType].length,
    duration: 0.5,
    easing: "linear",
    decimalPlaces: 0,
    onComplete: () => {
      setCurrentCount(filteredIndexes[sortType].length);
    },
  });

  const { accordion, accordionSummary, accordionDetails, descriptionText } =
    sxStyles;
  return (
    <Box sx={{ width: 1 }}>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          variant="titleMedium"
          weight="bold"
          sx={{ textTransform: "lowercase" }}
        >
          {value}
          {` ${translations.filters.results}`}
        </Typography>
        <ClearFiltersButton />
      </Stack>
      {translations.filters.filterTypes.map((filterType, i) => (
        <Accordion sx={accordion} defaultExpanded key={`filter-${i}`}>
          <AccordionSummary
            sx={accordionSummary}
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography weight="bold">{filterType.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetails}>
            <Typography variant="titleSmall" fontSize={13} sx={descriptionText}>
              {filterType.description}
            </Typography>
            {filterType.filter === "baggageAllowance" && <BaggageAllowance />}
            {filterType.filter === "travellerTypes" && <TravellerTypesFilter />}
            {filterType.filter === "stops" && <Stops />}
            {filterType.filter === "departureTime" && <DepartureTimeFilter />}
            {filterType.filter === "maxDurations" && <DurationFilter />}
            {filterType.filter === "preferredAirlines" &&
              allAirlines.length && <PreferredAirlinesFilter />}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  accordion: {
    boxShadow: 0,
    p: 0,
    my: 1,
    "&:before": {
      opacity: 0,
    },
    "&.Mui-expanded": {
      m: 0,
      mt: 1,
    },
  },
  accordionSummary: {
    borderBottom: "2px solid",
    borderColor: "primary.main",
    p: 0,
    m: 0,
    minHeight: 0,
    "&.Mui-expanded": {
      m: 0,
      minHeight: 0,
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
      color: "black.main",
    },
    "& .MuiAccordionSummary-content": {
      my: 1,
      mx: 0,
    },
  },
  accordionDetails: {
    pt: 1,
    pl: 0,
    pr: 0,
  },
  descriptionText: {
    mb: 1,
  },
};
